<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import Input from 'dashboard/components-next/input/Input.vue';
import Select from 'dashboard/components-next/select/Select.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import {
  applyProviderPreset,
  buildEmailConnectionPayload,
  detectProvider,
  stateFromInbox,
  validateEmailConnection,
} from './emailConnectionPresets';

const props = defineProps({
  inbox: { type: Object, required: true },
});

const { t } = useI18n();
const store = useStore();

const form = ref(stateFromInbox(props.inbox));
const provider = ref(detectProvider(props.inbox.imap_address));
const showAdvanced = ref(false);
const isSaving = ref(false);

watch(
  () => props.inbox.id,
  () => {
    form.value = stateFromInbox(props.inbox);
    provider.value = detectProvider(props.inbox.imap_address);
  }
);

const providerOptions = computed(() =>
  ['hosting', 'hostinger', 'titan', 'zoho', 'locaweb', 'custom'].map(value => ({
    value,
    label: t(`INBOX_MGMT.EMAIL_CONNECTION.PROVIDERS.${value.toUpperCase()}`),
  }))
);
const imapAuthOptions = ['plain', 'login', 'cram-md5'].map(value => ({
  value,
  label: value,
}));
const smtpAuthOptions = [
  'plain',
  'login',
  'cram-md5',
  'xoauth',
  'xoauth2',
  'ntlm',
  'gssapi',
].map(value => ({ value, label: value }));
const verifyOptions = [
  'none',
  'peer',
  'client_once',
  'fail_if_no_peer_cert',
].map(value => ({ value, label: value }));
const securityOptions = computed(() =>
  ['ssl', 'starttls', 'none'].map(value => ({
    value,
    label: t(`INBOX_MGMT.EMAIL_CONNECTION.SECURITY.${value.toUpperCase()}`),
  }))
);

const statusItems = computed(() => [
  {
    key: 'receive',
    label: t('INBOX_MGMT.EMAIL_CONNECTION.STATUS_RECEIVE'),
    active: Boolean(props.inbox.imap_enabled),
  },
  {
    key: 'send',
    label: t('INBOX_MGMT.EMAIL_CONNECTION.STATUS_SEND'),
    active: Boolean(props.inbox.imap_enabled && props.inbox.smtp_enabled),
  },
]);

const passwordPlaceholder = computed(() =>
  props.inbox.imap_password_configured
    ? t('INBOX_MGMT.EMAIL_CONNECTION.PASSWORD_SAVED')
    : t('INBOX_MGMT.EMAIL_CONNECTION.PASSWORD_PLACEHOLDER')
);

const onProviderChange = value => {
  provider.value = value;
  if (value === 'custom') {
    showAdvanced.value = true;
    return;
  }
  form.value = applyProviderPreset(form.value, value, form.value.login);
};

const onLoginBlur = () => {
  if (provider.value === 'custom') return;
  if (form.value.imapAddress && provider.value !== 'hosting') return;
  form.value = applyProviderPreset(
    form.value,
    provider.value,
    form.value.login
  );
};

// Server errors without a readable reason (e.g. "Internal Server Error") become a humane message.
const saveErrorMessage = error => {
  const message = String(error?.message || '').trim();
  const isTechnical =
    !message ||
    /internal server error|request failed|network error|timeout of/i.test(
      message
    );
  return isTechnical
    ? t('INBOX_MGMT.EMAIL_CONNECTION.ERRORS.GENERIC')
    : message;
};

const save = async () => {
  const missing = validateEmailConnection(form.value, props.inbox);
  if (missing) {
    useAlert(t(`INBOX_MGMT.EMAIL_CONNECTION.ERRORS.${missing}`));
    return;
  }
  isSaving.value = true;
  try {
    await store.dispatch('inboxes/updateInboxIMAP', {
      id: props.inbox.id,
      formData: false,
      channel: buildEmailConnectionPayload(form.value),
    });
    form.value = { ...form.value, password: '', smtpPassword: '' };
    useAlert(
      form.value.receiveEnabled
        ? t('INBOX_MGMT.EMAIL_CONNECTION.SAVED')
        : t('INBOX_MGMT.EMAIL_CONNECTION.DISABLED')
    );
  } catch (error) {
    useAlert(saveErrorMessage(error));
  } finally {
    isSaving.value = false;
  }
};
</script>

<template>
  <section
    class="mt-4 rounded-2xl border border-n-weak bg-n-solid-1 shadow-sm dark:border-n-strong"
    aria-labelledby="email-connection-title"
  >
    <header
      class="flex flex-wrap items-start justify-between gap-3 border-b border-n-weak px-5 py-4"
    >
      <div class="flex min-w-0 items-start gap-3">
        <span
          class="grid size-9 shrink-0 place-items-center rounded-xl bg-n-brand/10 text-n-brand"
          aria-hidden="true"
        >
          <span class="i-hugeicons-mail-01 size-[18px]" />
        </span>
        <div class="min-w-0">
          <h3
            id="email-connection-title"
            class="mb-0 text-base font-semibold text-n-slate-12"
          >
            {{ t('INBOX_MGMT.EMAIL_CONNECTION.TITLE') }}
          </h3>
          <p class="mb-0 mt-0.5 max-w-2xl text-xs text-n-slate-11">
            {{ t('INBOX_MGMT.EMAIL_CONNECTION.DESCRIPTION') }}
          </p>
        </div>
      </div>
      <div class="flex flex-wrap items-center gap-2">
        <span
          v-for="item in statusItems"
          :key="item.key"
          class="inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-xs font-medium"
          :class="
            item.active
              ? 'bg-n-teal-3 text-n-teal-11'
              : 'bg-n-alpha-2 text-n-slate-11'
          "
        >
          <span
            class="size-1.5 rounded-full"
            :class="item.active ? 'bg-n-teal-9' : 'bg-n-slate-8'"
          />
          {{ item.label }}:
          {{
            item.active
              ? t('INBOX_MGMT.EMAIL_CONNECTION.ACTIVE')
              : t('INBOX_MGMT.EMAIL_CONNECTION.INACTIVE')
          }}
        </span>
      </div>
    </header>

    <form class="grid gap-5 px-5 py-4" @submit.prevent="save">
      <label
        class="flex items-start justify-between gap-4 rounded-xl border border-n-weak bg-n-solid-2 px-4 py-3"
      >
        <span>
          <span class="block text-sm font-medium text-n-slate-12">
            {{ t('INBOX_MGMT.EMAIL_CONNECTION.RECEIVE_LABEL') }}
          </span>
          <span class="block text-xs text-n-slate-11">
            {{ t('INBOX_MGMT.EMAIL_CONNECTION.RECEIVE_HELP') }}
          </span>
        </span>
        <Switch v-model="form.receiveEnabled" />
      </label>

      <template v-if="form.receiveEnabled">
        <div class="grid gap-4 md:grid-cols-2">
          <div class="grid gap-1.5">
            <span class="text-sm font-medium text-n-slate-12">
              {{ t('INBOX_MGMT.EMAIL_CONNECTION.PROVIDER') }}
            </span>
            <Select
              :model-value="provider"
              :options="providerOptions"
              class="!w-full [&>select]:w-full"
              @update:model-value="onProviderChange"
            />
          </div>
          <Input
            v-model="form.login"
            type="email"
            autocomplete="username"
            :label="t('INBOX_MGMT.EMAIL_CONNECTION.LOGIN')"
            :placeholder="t('INBOX_MGMT.EMAIL_CONNECTION.LOGIN_PLACEHOLDER')"
            @blur="onLoginBlur"
          />
          <Input
            v-model="form.password"
            type="password"
            autocomplete="new-password"
            show-password-toggle
            :label="t('INBOX_MGMT.EMAIL_CONNECTION.PASSWORD')"
            :placeholder="passwordPlaceholder"
            :message="
              inbox.imap_password_configured
                ? t('INBOX_MGMT.EMAIL_CONNECTION.PASSWORD_KEEP_HINT')
                : ''
            "
          />
          <Input
            v-model="form.imapAddress"
            :label="t('INBOX_MGMT.EMAIL_CONNECTION.IMAP_SERVER')"
            placeholder="mail.seudominio.com.br"
          />
        </div>

        <div class="grid gap-3 rounded-xl border border-n-weak p-4">
          <label class="flex items-start justify-between gap-4">
            <span>
              <span class="block text-sm font-medium text-n-slate-12">
                {{ t('INBOX_MGMT.EMAIL_CONNECTION.SEND_LABEL') }}
              </span>
              <span class="block text-xs text-n-slate-11">
                {{ t('INBOX_MGMT.EMAIL_CONNECTION.SEND_HELP') }}
              </span>
            </span>
            <Switch v-model="form.sendEnabled" />
          </label>
          <div v-if="form.sendEnabled" class="grid gap-4 md:grid-cols-2">
            <Input
              v-model="form.smtpAddress"
              :label="t('INBOX_MGMT.EMAIL_CONNECTION.SMTP_SERVER')"
              placeholder="mail.seudominio.com.br"
            />
            <label
              class="flex items-center gap-2 self-end pb-2 text-sm text-n-slate-12"
            >
              <input v-model="form.sameCredentials" type="checkbox" />
              {{ t('INBOX_MGMT.EMAIL_CONNECTION.SAME_CREDENTIALS') }}
            </label>
            <template v-if="!form.sameCredentials">
              <Input
                v-model="form.smtpLogin"
                autocomplete="off"
                :label="t('INBOX_MGMT.EMAIL_CONNECTION.SMTP_LOGIN')"
              />
              <Input
                v-model="form.smtpPassword"
                type="password"
                autocomplete="new-password"
                show-password-toggle
                :label="t('INBOX_MGMT.EMAIL_CONNECTION.SMTP_PASSWORD')"
                :placeholder="
                  inbox.smtp_password_configured
                    ? t('INBOX_MGMT.EMAIL_CONNECTION.PASSWORD_SAVED')
                    : ''
                "
              />
            </template>
          </div>
        </div>

        <div class="rounded-xl border border-n-weak">
          <button
            type="button"
            class="flex w-full items-center justify-between gap-2 px-4 py-3 text-left text-sm font-medium text-n-slate-12"
            :aria-expanded="showAdvanced"
            @click="showAdvanced = !showAdvanced"
          >
            <span>{{ t('INBOX_MGMT.EMAIL_CONNECTION.ADVANCED') }}</span>
            <span
              class="i-hugeicons-arrow-down-01 size-4 text-n-slate-11 transition-transform"
              :class="{ 'rotate-180': showAdvanced }"
            />
          </button>
          <div
            v-if="showAdvanced"
            class="grid gap-4 border-t border-n-weak px-4 py-4 md:grid-cols-2"
          >
            <Input
              v-model="form.imapPort"
              type="number"
              :label="t('INBOX_MGMT.EMAIL_CONNECTION.IMAP_PORT')"
            />
            <div class="grid gap-1.5">
              <span class="text-sm font-medium text-n-slate-12">
                {{ t('INBOX_MGMT.EMAIL_CONNECTION.IMAP_AUTH') }}
              </span>
              <Select
                v-model="form.imapAuthentication"
                :options="imapAuthOptions"
                class="!w-full [&>select]:w-full"
              />
            </div>
            <label class="flex items-center gap-2 text-sm text-n-slate-12">
              <input v-model="form.imapSsl" type="checkbox" />
              {{ t('INBOX_MGMT.EMAIL_CONNECTION.IMAP_SSL') }}
            </label>
            <span />
            <template v-if="form.sendEnabled">
              <Input
                v-model="form.smtpPort"
                type="number"
                :label="t('INBOX_MGMT.EMAIL_CONNECTION.SMTP_PORT')"
              />
              <div class="grid gap-1.5">
                <span class="text-sm font-medium text-n-slate-12">
                  {{ t('INBOX_MGMT.EMAIL_CONNECTION.SMTP_SECURITY') }}
                </span>
                <Select
                  v-model="form.smtpSecurity"
                  :options="securityOptions"
                  class="!w-full [&>select]:w-full"
                />
              </div>
              <Input
                v-model="form.smtpDomain"
                :label="t('INBOX_MGMT.EMAIL_CONNECTION.SMTP_DOMAIN')"
              />
              <div class="grid gap-1.5">
                <span class="text-sm font-medium text-n-slate-12">
                  {{ t('INBOX_MGMT.EMAIL_CONNECTION.SMTP_AUTH') }}
                </span>
                <Select
                  v-model="form.smtpAuthentication"
                  :options="smtpAuthOptions"
                  class="!w-full [&>select]:w-full"
                />
              </div>
              <div class="grid gap-1.5">
                <span class="text-sm font-medium text-n-slate-12">
                  {{ t('INBOX_MGMT.EMAIL_CONNECTION.SMTP_VERIFY') }}
                </span>
                <Select
                  v-model="form.smtpVerifyMode"
                  :options="verifyOptions"
                  class="!w-full [&>select]:w-full"
                />
              </div>
            </template>
          </div>
        </div>
      </template>

      <div
        class="flex flex-wrap items-center justify-between gap-3 border-t border-n-weak pt-4"
      >
        <p class="mb-0 max-w-xl text-xs text-n-slate-11">
          {{ t('INBOX_MGMT.EMAIL_CONNECTION.FOOTER_HELP') }}
        </p>
        <Button
          type="submit"
          :label="
            form.receiveEnabled
              ? t('INBOX_MGMT.EMAIL_CONNECTION.SAVE_AND_TEST')
              : t('INBOX_MGMT.EMAIL_CONNECTION.SAVE')
          "
          icon="i-hugeicons-plug-01"
          :is-loading="isSaving"
          :disabled="isSaving"
        />
      </div>
    </form>
  </section>
</template>
